/** A blank line ends a frame, whichever line ending the sender uses. */
const SSE_FRAME_BOUNDARY = /\r?\n\r?\n/;

export interface SseFrame {
  /** The server's sequence number, which is also the replay cursor on reconnect. */
  id?: string;
  event?: string;
  data: string;
}

/**
 * Reads an SSE body into frames.
 *
 * Comment lines are dropped, which is the whole of how heartbeats are handled: the server keeps
 * an idle stream open with a genuine `: keep-alive` comment precisely so it reaches no event
 * handler, and a client that surfaced them would show an empty agent message every fifteen
 * seconds. The corollary is the thing not to build on top of this — a turn can legitimately emit
 * nothing for minutes while a model works, so silence between frames says nothing about whether
 * the turn is alive, and no timeout here tries to guess.
 *
 * Written against the response body rather than `EventSource` because the reconnect cursor has to
 * be ours: `EventSource` sends `Last-Event-ID` only on its own automatic retry, and it cannot be
 * given an abort signal, so a component unmounting mid-turn would leak a connection that keeps
 * retrying.
 */
export const readSseFrames = async function* (
  body: ReadableStream<Uint8Array>,
  signal?: AbortSignal,
): AsyncGenerator<SseFrame> {
  const reader = body.getReader();
  const decoder = new TextDecoder();
  let buffered = '';

  try {
    for (;;) {
      if (signal?.aborted === true) {
        break;
      }

      const { done, value } = await reader.read();
      if (done) {
        break;
      }

      buffered += decoder.decode(value, { stream: true });

      // A frame ends at a blank line. Anything after the last one is a partial frame and stays
      // buffered — a chunk boundary falls wherever the network puts it, not on frame boundaries.
      //
      // CRLF is accepted as well as LF. Our own server writes LF, so this is unreachable today;
      // it is here because the failure it prevents is silent and total — an intermediary that
      // normalised line endings would leave every frame unterminated, and the stream would hang
      // looking exactly like a model that had stopped talking.
      for (;;) {
        const boundary = SSE_FRAME_BOUNDARY.exec(buffered);
        if (boundary === null) {
          break;
        }

        const block = buffered.slice(0, boundary.index);
        buffered = buffered.slice(boundary.index + boundary[0].length);

        const frame = parseFrame(block);
        if (frame !== null) {
          yield frame;
        }
      }
    }
  } finally {
    // Releasing rather than cancelling: the caller owns the response and may be abandoning this
    // read to reconnect, in which case cancelling here would tear down its replacement's body.
    reader.releaseLock();
  }
};

const parseFrame = (block: string): SseFrame | null => {
  const frame: SseFrame = { data: '' };
  const data: string[] = [];

  for (const rawLine of block.split('\n')) {
    // A trailing carriage return is framing, not part of the value; left on, it would be
    // appended to every event name and payload.
    const line = rawLine.endsWith('\r') ? rawLine.slice(0, -1) : rawLine;

    if (line === '' || line.startsWith(':')) {
      continue;
    }

    const separator = line.indexOf(':');
    const field = separator === -1 ? line : line.slice(0, separator);
    // One optional space after the colon is part of the framing, not of the value.
    const raw = separator === -1 ? '' : line.slice(separator + 1);
    const value = raw.startsWith(' ') ? raw.slice(1) : raw;

    if (field === 'id') {
      frame.id = value;
    } else if (field === 'event') {
      frame.event = value;
    } else if (field === 'data') {
      data.push(value);
    }
  }

  if (frame.id === undefined && frame.event === undefined && data.length === 0) {
    return null;
  }

  return { ...frame, data: data.join('\n') };
};
